/**
 * Filters the full onboarding state response to only include the requested keys.
 * This enables a centralized caching strategy where we always fetch all keys
 * but consumers only see the keys they requested.
 *
 * @param data - The full onboarding state response from the GraphQL query
 * @param requestedKeys - The key(s) that the consumer originally requested
 * @returns A filtered response containing only the requested keys
 */
export const filterOnboardingState = <
	T extends { onboardingState: Array<{ key: string; value: string | null }> },
>(
	data: T | null | undefined,
	requestedKeys: string | string[],
): T | undefined => {
	if (!data?.onboardingState) {
		// Convert null to undefined to maintain backward compatibility with consumers
		// that expect `T | undefined` rather than `T | null | undefined`
		return data ?? undefined;
	}

	const keysArray = Array.isArray(requestedKeys) ? requestedKeys : [requestedKeys];
	const keySet = new Set(keysArray);

	return {
		...data,
		onboardingState: data.onboardingState.filter(({ key }) => {
			// Check both the full key and the parsed key (without prefix)
			// Keys come back as "{prefix}:{property}" format
			const parsedKey = key.split(':')[1] || key;
			return keySet.has(key) || keySet.has(parsedKey);
		}),
	} as T;
};
